import {
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode
} from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { Overlay, OverlayTrigger, Tooltip } from 'react-bootstrap';
import classNames from 'classnames';
import { useAppContext } from '@/Main';
import { getColor } from '@/helpers/utils';

const MORE_FALLBACK_WIDTH = 52;

export type ToolTabItem = {
  key: string;
  label: ReactNode;
  to: string;
  active?: boolean;
  disabled?: boolean;
  badge?: ReactNode;
  state?: { path?: string; query?: string | null };
  onClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  tooltip?: string;
};

/** How many leading tabs fit. The rest go behind the "..." control. */
export function visibleToolTabCount(
  widths: number[],
  available: number,
  moreWidth = MORE_FALLBACK_WIDTH
): number {
  if (!widths.length) return 0;
  if (available <= 0 || !widths.some(width => width > 0)) return widths.length;

  let used = 0;
  for (let index = 0; index < widths.length; index += 1) {
    const remaining = widths.length - index - 1;
    const reserve = remaining > 0 ? moreWidth : 0;
    if (used + widths[index] + reserve <= available) {
      used += widths[index];
      continue;
    }
    return Math.max(1, index);
  }
  return widths.length;
}

function assertVisibleToolTabCount() {
  const expect = (
    widths: number[],
    available: number,
    more: number,
    expected: number
  ) => {
    const actual = visibleToolTabCount(widths, available, more);
    if (actual !== expected) {
      throw new Error(
        `visibleToolTabCount(${JSON.stringify(widths)}, ${available}, ${more}) = ${actual}, expected ${expected}`
      );
    }
  };
  expect([40, 40, 40], 200, 52, 3);
  expect([80, 80, 80, 80], 212, 52, 2);
  expect([200], 50, 52, 1);
  expect([], 100, 52, 0);
  expect([80, 80], 0, 52, 2);
}

if (import.meta.env.DEV) assertVisibleToolTabCount();

function tabBackground(theme: string, active: boolean) {
  if (active && theme === 'dark') return 'rgba(0,0,0,.2)';
  if (active && theme === 'light') return 'rgba(0,0,0,.02)';
  return '';
}

function TabFace({
  label,
  badge,
  active,
  disabled,
  theme,
  compact
}: {
  label: ReactNode;
  badge?: ReactNode;
  active: boolean;
  disabled?: boolean;
  theme: string;
  compact?: boolean;
}) {
  return (
    <div
      className={classNames(
        'tool-tab-pad py-2 pt-3 d-flex align-items-center text-center',
        compact && 'tool-tab-more',
        !disabled && 'cursor-pointer',
        active && 'active-l text-primary fw-semi-bold'
      )}
      style={{
        backgroundColor: tabBackground(theme, active),
        borderBottom: '2px solid transparent',
        borderColor: active
          ? getColor('navbar-vertical-default-link-active-color')
          : 'transparent'
      }}
    >
      <h6
        className={disabled ? 'disabled-link' : 'text-800'}
        style={{
          fontSize: '.75rem',
          whiteSpace: 'nowrap',
          fontWeight: compact ? 600 : undefined
        }}
      >
        {label}
        {badge}
      </h6>
    </div>
  );
}

function TabLink({
  item,
  children
}: {
  item: ToolTabItem;
  children: ReactNode;
}) {
  const className = classNames(
    'nav-item flex-shrink-0',
    item.disabled && !item.tooltip && 'disabled-link',
    item.tooltip && 'opacity-50'
  );
  const style = { textDecoration: 'none', color: 'unset', marginBottom: -1 };

  if (item.disabled) {
    const node = (
      <div className={className} style={style} aria-disabled="true">
        {children}
      </div>
    );
    if (!item.tooltip) return node;
    return (
      <OverlayTrigger
        placement="top"
        overlay={
          <Tooltip id={`tool-tab-${item.key}`}>{item.tooltip}</Tooltip>
        }
      >
        {node}
      </OverlayTrigger>
    );
  }

  return (
    <NavLink
      to={item.to}
      state={item.state}
      style={style}
      className={className}
      onClick={item.onClick}
    >
      {children}
    </NavLink>
  );
}

function MenuEntry({
  item,
  onDone
}: {
  item: ToolTabItem;
  onDone: () => void;
}) {
  if (item.disabled) {
    return (
      <span
        className="dropdown-item disabled d-flex align-items-center justify-content-between gap-2 py-2"
        title={item.tooltip}
      >
        <span>{item.label}</span>
        {item.badge}
      </span>
    );
  }

  return (
    <NavLink
      to={item.to}
      state={item.state}
      className={classNames(
        'dropdown-item d-flex align-items-center justify-content-between gap-2 py-2',
        item.active && 'active'
      )}
      onClick={event => {
        onDone();
        item.onClick?.(event);
      }}
    >
      <span>{item.label}</span>
      {item.badge}
    </NavLink>
  );
}

const ToolTabs = ({ items }: { items: ToolTabItem[] }) => {
  const {
    config: { theme }
  } = useAppContext();
  const { pathname } = useLocation();
  const headerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const moreBtnRef = useRef<HTMLButtonElement>(null);
  const widthsRef = useRef<number[]>([]);
  const [visibleCount, setVisibleCount] = useState(items.length);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useLayoutEffect(() => {
    const header = headerRef.current;
    const measure = measureRef.current;
    if (!header || !measure) return undefined;

    const update = () => {
      const nodes = Array.from(
        measure.querySelectorAll<HTMLElement>('[data-tool-tab]')
      );
      const widths = nodes.map((node, index) => {
        const width = node.offsetWidth;
        if (width) widthsRef.current[index] = width;
        return width || widthsRef.current[index] || 0;
      });
      const moreWidth =
        measure.querySelector<HTMLElement>('[data-tool-tab-more]')
          ?.offsetWidth || MORE_FALLBACK_WIDTH;
      const next = visibleToolTabCount(
        widths,
        header.clientWidth,
        moreWidth
      );
      setVisibleCount(current => (current === next ? current : next));
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(header);
    return () => observer.disconnect();
  }, [items]);

  const visible = items.slice(0, visibleCount);
  const overflow = items.slice(visibleCount);
  const overflowActive = overflow.some(item => item.active);
  const face = (item: ToolTabItem) => (
    <TabFace
      label={item.label}
      badge={item.badge}
      active={Boolean(item.active)}
      disabled={Boolean(item.disabled) && !item.tooltip}
      theme={theme}
    />
  );

  return (
    <div className="my-3">
      <div className="p-0 m-0 bg-transparent">
        <div
          ref={headerRef}
          className="d-flex align-items-stretch position-relative border-bottom border-1"
        >
          <div
            ref={measureRef}
            aria-hidden
            className="d-flex flex-nowrap position-absolute"
            style={{
              visibility: 'hidden',
              pointerEvents: 'none',
              height: 0,
              overflow: 'hidden',
              insetInlineStart: 0,
              top: 0
            }}
          >
            {items.map(item => (
              <div
                key={item.key}
                data-tool-tab
                className="nav-item flex-shrink-0"
              >
                {face(item)}
              </div>
            ))}
            <button
              type="button"
              tabIndex={-1}
              data-tool-tab-more
              className="nav-item flex-shrink-0 border-0 bg-transparent p-0"
            >
              <TabFace label="..." active={false} theme={theme} compact />
            </button>
          </div>

          {visible.map(item => (
            <TabLink key={item.key} item={item}>
              {face(item)}
            </TabLink>
          ))}

          {overflow.length > 0 && (
            <>
              <button
                ref={moreBtnRef}
                type="button"
                className="nav-item flex-shrink-0 border-0 bg-transparent p-0"
                style={{ marginBottom: -1, marginRight: 'auto' }}
                aria-expanded={open}
                aria-haspopup="menu"
                aria-label="تب‌های بیشتر"
                onClick={() => setOpen(value => !value)}
              >
                <TabFace
                  label="..."
                  active={overflowActive}
                  theme={theme}
                  compact
                />
              </button>
              <Overlay
                target={moreBtnRef}
                show={open}
                placement="bottom-end"
                rootClose
                onHide={() => setOpen(false)}
                container={
                  typeof document !== 'undefined' ? document.body : undefined
                }
                popperConfig={{ strategy: 'fixed' }}
              >
                {({
                  placement: _placement,
                  arrowProps: _arrowProps,
                  show: _show,
                  popper: _popper,
                  hasDoneInitialMeasure: _hasDoneInitialMeasure,
                  ...overlayProps
                }) => (
                  <div
                    {...overlayProps}
                    role="menu"
                    className="dropdown-menu show border py-1 shadow-sm"
                    style={{
                      ...overlayProps.style,
                      minWidth: 200,
                      maxHeight: 320,
                      overflowY: 'auto',
                      zIndex: 1080
                    }}
                  >
                    {overflow.map(item => (
                      <MenuEntry
                        key={item.key}
                        item={item}
                        onDone={() => setOpen(false)}
                      />
                    ))}
                  </div>
                )}
              </Overlay>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default ToolTabs;
